import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Container,
  Divider,
} from '@mui/material';
import {
  Login as LoginIcon,
  Security as SecurityIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useAuth } from '../auth/AuthProvider';
import logger from '../utils/logger';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isLoading, error, clearError } = useAuth();
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Get the return URL from location state
  const returnUrl = (location.state as any)?.returnUrl || '/';

  useEffect(() => {
    // If already authenticated, redirect to return URL
    if (isAuthenticated) {
      logger.navigationEvent('/login', returnUrl);
      navigate(returnUrl, { replace: true });
    }
  }, [isAuthenticated, navigate, returnUrl]);

  useEffect(() => {
    // Clear any existing errors when component mounts
    if (error) {
      clearError();
    }
  }, []);

  const handleLogin = async () => {
    try {
      setIsLoggingIn(true);
      clearError();
      
      logger.authEvent('login_button_clicked', {
        returnUrl,
        userAgent: navigator.userAgent,
      });

      await login();
    } catch (loginError) {
      logger.error('Login initiation failed', {
        error: loginError instanceof Error ? loginError.message : 'Unknown error',
        returnUrl,
      });
      setIsLoggingIn(false);
    }
  };

  const handleDemoLogin = () => {
    logger.userAction('demo_info_requested');
    // This would typically show demo credentials or instructions
    alert('Demo: Contact your administrator for login credentials.');
  };

  return (
    <Box
      sx={{
        backgroundColor: '#FFF8F5',
        minHeight: '100vh',
      }}
    >
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
            justifyContent: 'center',
            py: 4,
          }}
        >
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{
              color: '#FAAF90',
              fontFamily: "'Tenor Sans', 'Roboto', 'Helvetica', 'Arial', sans-serif",
            }}
          >
            Hinata PAL
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please sign in to continue
          </Typography>
        </Box>

        {/* Login Card */}
        <Card elevation={8}>
          <CardContent sx={{ p: 4 }}>
            {error && (
              <Alert 
                severity="error" 
                sx={{ mb: 3 }}
                onClose={clearError}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Authentication Failed
                </Typography>
                <Typography variant="body2">
                  {error}
                </Typography>
              </Alert>
            )}

            {returnUrl !== '/' && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  You need to sign in to access this page.
                </Typography>
              </Alert>
            )}

            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <SecurityIcon
                sx={{
                  fontSize: 60,
                  color: '#FAAF90',
                  mb: 2
                }}
              />
              <Typography
                variant="h5"
                gutterBottom
                sx={{
                  fontFamily: "'Tenor Sans', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                }}
              >
                Secure Sign In
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sign in using your organization's credentials through our secure authentication system.
              </Typography>
            </Box>

            <Button
              variant="contained"
              size="large"
              fullWidth
              startIcon={isLoggingIn || isLoading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
              onClick={handleLogin}
              disabled={isLoggingIn || isLoading}
              sx={{
                py: 1.5,
                mb: 2,
                fontSize: '1.1rem',
                backgroundColor: '#FAAF90',
                fontFamily: "'Tenor Sans', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                '&:hover': {
                  backgroundColor: '#FCC4AD',
                },
                '&:active': {
                  backgroundColor: '#E59873',
                },
              }}
            >
              {isLoggingIn || isLoading ? 'Signing In...' : 'Sign In with SSO'}
            </Button>

            <Divider sx={{ my: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Need Help?
              </Typography>
            </Divider>

            <Button
              variant="outlined"
              fullWidth
              startIcon={<InfoIcon />}
              onClick={handleDemoLogin}
              disabled={isLoggingIn || isLoading}
              sx={{
                mb: 2,
                borderColor: '#FAAF90',
                color: '#FAAF90',
                fontFamily: "'Tenor Sans', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                '&:hover': {
                  borderColor: '#E59873',
                  backgroundColor: 'rgba(250, 175, 144, 0.08)',
                },
              }}
            >
              Demo Information
            </Button>
          </CardContent>
        </Card>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" color="text.secondary">
            © 2024 Hinata PAL. All rights reserved.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Version {import.meta.env.VITE_VERSION || '1.0.0'}
          </Typography>
        </Box>
      </Box>
      </Container>
    </Box>
  );
};

export default Login;